import { Box, Flex, Text } from '@chakra-ui/react';
import { Modal, Button, useToast } from '@morressier/alembic';
import {
  ICommandResponse,
  useEditorialWithdrawAcceptedReviewInvitation,
  useEditorialWithdrawAwaitingRevisionReviewInvitation,
  useEditorialWithdrawInvitedReviewInvitation,
  useEditorialWithdrawRevisionAssessmentReviewInvitation,
  useEditorialWithdrawRevisionInvitedReviewInvitation
} from '../../../api/commands';
import {
  IPeerReviewWithdrawActions,
  IWorkPhase
} from '@publishing-engine/domain/models';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserSlash } from '@fortawesome/pro-light-svg-icons';
import ProtectedReviewWorkflowAction from 'src/components/ProtectedAction/ProtectedReviewWorkflowAction';

type IProps = {
  protectedWFActionName: IPeerReviewWithdrawActions;
  onClose: () => void;
  phase: IWorkPhase;
  submissionId: string;
  submissionContextId: string;
  reviewRequestId: string;
  reviewerId: string;
  organizationId: string;
};

export const WithdrawReviewInvitationModal = ({
  protectedWFActionName,
  onClose,
  phase,
  submissionId,
  submissionContextId,
  reviewRequestId,
  reviewerId,
  organizationId
}: IProps) => {
  const submissionWorkflowQueryKey = ['submission-workflow', submissionId];
  const reviewQueueQueryKey = [
    'submission-context',
    submissionContextId,
    submissionId,
    'reviewer-invitations'
  ];
  const reviewsQueryKey = [
    'submission-context',
    submissionContextId,
    submissionId,
    'reviews'
  ];
  const { mutateAsync: mutateWithdrawInvitedReviewInvitation } =
    useEditorialWithdrawInvitedReviewInvitation({
      phase,
      submissionId,
      submissionContextId,
      queryKeys: [
        submissionWorkflowQueryKey,
        reviewsQueryKey,
        reviewQueueQueryKey
      ]
    });

  const { mutateAsync: mutateWithdrawAcceptedReviewInvitation } =
    useEditorialWithdrawAcceptedReviewInvitation({
      phase,
      submissionId,
      submissionContextId,
      queryKeys: [
        submissionWorkflowQueryKey,
        reviewsQueryKey,
        reviewQueueQueryKey
      ]
    });

  const { mutateAsync: mutateWithdrawAwaitingRevisionReviewInvitation } =
    useEditorialWithdrawAwaitingRevisionReviewInvitation({
      phase,
      submissionId,
      submissionContextId,
      queryKeys: [
        submissionWorkflowQueryKey,
        reviewsQueryKey,
        reviewQueueQueryKey
      ]
    });

  const { mutateAsync: mutateWithdrawRevisionInvitedReviewInvitation } =
    useEditorialWithdrawRevisionInvitedReviewInvitation({
      phase,
      submissionId,
      submissionContextId,
      queryKeys: [
        submissionWorkflowQueryKey,
        reviewsQueryKey,
        reviewQueueQueryKey
      ]
    });

  const { mutateAsync: mutateWithdrawRevisionAssessmentReviewInvitation } =
    useEditorialWithdrawRevisionAssessmentReviewInvitation({
      phase,
      submissionId,
      submissionContextId,
      queryKeys: [
        submissionWorkflowQueryKey,
        reviewsQueryKey,
        reviewQueueQueryKey
      ]
    });

  const toast = useToast();

  const withdrawMutations: Record<
    IPeerReviewWithdrawActions,
    () => Promise<ICommandResponse>
  > = {
    EditorialWithdrawInvitedReviewInvitation: () =>
      mutateWithdrawInvitedReviewInvitation({
        reviewerId,
        reviewRequestId,
        organizationId
      }),
    EditorialWithdrawAcceptedReviewInvitation: () =>
      mutateWithdrawAcceptedReviewInvitation({
        reviewerId,
        reviewRequestId,
        organizationId
      }),
    EditorialWithdrawAwaitingRevisionReviewInvitation: () =>
      mutateWithdrawAwaitingRevisionReviewInvitation({
        reviewerId,
        reviewRequestId,
        organizationId
      }),
    EditorialWithdrawRevisionInvitedReviewInvitation: () =>
      mutateWithdrawRevisionInvitedReviewInvitation({
        reviewerId,
        reviewRequestId,
        organizationId
      }),
    EditorialWithdrawRevisionAssessmentReviewInvitation: () =>
      mutateWithdrawRevisionAssessmentReviewInvitation({
        reviewerId,
        reviewRequestId,
        organizationId
      })
  };

  const handleWithdrawReviewer = async () => {
    const response = await withdrawMutations[protectedWFActionName]?.();

    if (response?.isSuccess) {
      toast({
        isClosable: true,
        variant: 'success',
        title: `Reviewer Withdrawn`,
        description: 'The reviewer was notified about this decisions via email.'
      });
    } else {
      toast({
        variant: 'error',
        title: 'Error while withdrawing reviewer'
      });
    }
    onClose();
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      width="448px"
      closeButtonProps={{
        hidden: true
      }}
    >
      <Flex padding={8} gap={4} flexDirection="column">
        <Box color="actionSecondaryCautionContent" marginStart={0}>
          <FontAwesomeIcon icon={faUserSlash} fontSize={40} />
        </Box>
        <Text textStyle="headingGroup">Withdraw Reviewer?</Text>
        <Text>
          This reviewer will be withdrawn from the current round and will be
          notified by email. You can still resend them an invitation in case
          you would like to bring them back into the peer review process.
        </Text>

        <Flex gap={2} justifyContent="flex-end">
          <Button onClick={onClose} variant="secondary">
            Cancel
          </Button>
          <ProtectedReviewWorkflowAction
            reviewId={reviewRequestId}
            actionName={protectedWFActionName}
          >
            <Button
              type="submit"
              variant="caution"
              onClick={handleWithdrawReviewer}
            >
              Withdraw Reviewer
            </Button>
          </ProtectedReviewWorkflowAction>
        </Flex>
      </Flex>
    </Modal>
  );
};
